import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import { 
  MODAL_STYLES, 
  MODAL_BEHAVIOR, 
  getBackdropClasses, 
  getSizeClasses,
  type BackdropType, 
  type ModalSize 
} from './modalConfig';

interface BaseModalProps {
  // Control básico
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  
  // Apariencia
  size?: ModalSize;
  backdrop?: BackdropType;
  className?: string;
  
  // Comportamiento
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  preventBodyScroll?: boolean;
  
  //  Accesibilidad
  title?: string;
  description?: string;
  autoFocus?: boolean;
  
  // Animación
  disableAnimation?: boolean;
  
  // Responsive
  fullScreenOnMobile?: boolean;
}

export const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  children,
  size = 'md',
  backdrop = 'blur',
  className = '',
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  preventBodyScroll = true,
  title,
  description,
  autoFocus = true,
  disableAnimation = false,
  fullScreenOnMobile = false
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  
  // Focus management
  useEffect(() => {
    if (isOpen) {
      // Guardar elemento con foco actual
      previousFocusRef.current = document.activeElement as HTMLElement;
      
      // Auto-focus al modal si está habilitado
      if (autoFocus && modalRef.current) {
        const firstFocusable = modalRef.current.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement;
        
        if (firstFocusable) {
          firstFocusable.focus();
        } else {
          modalRef.current.focus();
        }
      }
    } else {
      // Restaurar foco al cerrar
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    }
  }, [isOpen, autoFocus]);
  
  // Keyboard handling
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape' && closeOnEscape) {
      onClose();
    }
    
    // Trap focus dentro del modal
    if (event.key === 'Tab' && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements.length === 0) return;
      
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
      
      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          event.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          event.preventDefault();
        }
      }
    }
  }, [closeOnEscape, onClose]);
  
  //  Body scroll prevention
  useEffect(() => {
    if (isOpen && preventBodyScroll) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen, preventBodyScroll]);
  
  // Event listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);
  
  // Backdrop click handler
  const handleBackdropClick = useCallback((event: React.MouseEvent) => {
    if (event.target === event.currentTarget && closeOnBackdrop) {
      onClose();
    }
  }, [closeOnBackdrop, onClose]);
  
  // Animation classes
  const animationClasses = disableAnimation ? '' : `
    ${MODAL_STYLES.animations.backdrop.enter}
    ${MODAL_STYLES.animations.backdrop.enterFrom}
    ${MODAL_STYLES.animations.backdrop.enterTo}
  `;
  
  const contentAnimationClasses = disableAnimation ? '' : `
    ${MODAL_STYLES.animations.content.enter}
    ${MODAL_STYLES.animations.content.enterFrom}
    ${MODAL_STYLES.animations.content.enterTo}
  `;
  
  // Responsive classes
  const responsiveClasses = fullScreenOnMobile 
    ? 'w-full h-full sm:w-auto sm:h-auto sm:max-w-none sm:rounded-lg'
    : getSizeClasses(size);
  
  // Combined classes
  const backdropClasses = `
    fixed inset-0 flex items-center justify-center p-4
    ${getBackdropClasses(backdrop)}
    ${MODAL_STYLES.zIndex.modal}
    ${animationClasses}
  `.trim().replace(/\s+/g, ' ');
  
  const contentClasses = `
    relative
    ${MODAL_STYLES.container.base}
    ${responsiveClasses}
    ${contentAnimationClasses}
    ${className}
  `.trim().replace(/\s+/g, ' ');
  
  if (!isOpen) return null;
  
  const modalContent = (
    <div 
      className={backdropClasses}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      aria-describedby={description ? 'modal-description' : undefined}
    >
      <div
        ref={modalRef}
        className={contentClasses}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        {/* Close button */}
        {showCloseButton && (
          <button
            onClick={onClose}
            className={`
              absolute top-4 right-4 z-10
              ${MODAL_STYLES.header.closeButton}
            `}
            aria-label="Cerrar modal"
          >
            <FiX className="w-5 h-5" />
          </button>
        )}
        
        {/*  Content */}
        <div className={MODAL_STYLES.container.padding}>
          {children}
        </div>
      </div>
    </div>
  );
  
  //  Render en portal para evitar z-index issues
  return createPortal(modalContent, document.body);
};

// Hook personalizado para usar con BaseModal
export const useBaseModal = (initialState = false) => {
  const [isOpen, setIsOpen] = React.useState(initialState);
  
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);
  
  return {
    isOpen,
    open,
    close,
    toggle,
    setIsOpen
  };
};

export default BaseModal;